import React, { ReactElement } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import SearchPage from "./pages/SearchPage/SearchPage";
import NotFoundPage from "./pages/error/NotFoundPage";
import DefaultLayout from "./layout/DefaultLayout";
import AdminDefaultLayout from "./pages/admin/AdminDefaultLayout";
import ArchiveDefault from "./pages/admin/Archive/ArchiveDefault";
import { AppRole } from "./service/AppRoleService";
import { useAppSelector } from "@/store/hooks";
import {
  selectAppRoles,
  selectHasAnyRole,
  selectUserInfo,
} from "@/store/selectors/authSelectors";
import NoAccessPage from "./pages/error/NoAccessPage";
import ConnectionErrorPage from "./pages/error/ConnectionErrorPage";
import Logger from "@/utils/Logger";
import { useAuth } from "./hooks/useAuth";

interface RouteConfig {
  path: string;
  element: ReactElement;
  requiresAuth?: boolean;
  requiredRoles?: AppRole[];
  isPublic?: boolean;
}

interface ProtectedRouteProps {
  children: ReactElement;
  requiresAuth?: boolean;
  requiredRoles?: AppRole[];
  isAuthenticated: boolean;
  noAccessElement?: ReactElement;
}

const routeConfigs: RouteConfig[] = [
  {
    path: "/",
    element: <SearchPage />,
    requiresAuth: true,
    requiredRoles: [AppRole.SCRM_APP_ADMIN, AppRole.SCRM_APP_READ_ONLY],
  },
  {
    path: "/admin",
    element: <AdminDefaultLayout />,
    requiresAuth: true,
    requiredRoles: [AppRole.SCRM_APP_ADMIN, AppRole.SCRM_APP_READ_ONLY],
  },
  {
    path: "/admin/archive",
    element: <ArchiveDefault />,
    requiresAuth: true,
    requiredRoles: [AppRole.SCRM_APP_ADMIN],
  },
  {
    path: "/connection-error",
    element: <ConnectionErrorPage />,
    isPublic: true,
  },
  { path: "*", element: <NotFoundPage />, isPublic: true },
  { path: "/access-denied", element: <NoAccessPage />, isPublic: true },
];

const LoadingSpinner: React.FC<{ message: string }> = ({ message }) => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
      <p className="text-gray-600">{message}</p>
    </div>
  </div>
);

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiresAuth = false,
  requiredRoles = [],
  isAuthenticated,
  noAccessElement = <Navigate to="/access-denied" replace />,
}) => {
  // Use unified role selectors from userSlice
  const userRoles = useAppSelector(selectAppRoles);
  const hasRequiredRole = useAppSelector(
    requiredRoles.length > 0 ? selectHasAnyRole(requiredRoles) : () => true,
  );

  // Public routes don't require authentication
  if (!requiresAuth) {
    return children;
  }

  // Check authentication
  if (!isAuthenticated) {
    return <Navigate to="/access-denied" replace />;
  }

  // Enhanced role checking using unified selectors
  if (requiredRoles.length > 0) {
    if (!userRoles || userRoles.length === 0) {
      return noAccessElement;
    }

    if (!hasRequiredRole) {
      Logger.info(
        `Access denied: User lacks required roles ${requiredRoles.join(", ")}. User has: ${userRoles.join(", ")}`,
        ["ProtectedRoute"],
      );
      return <Navigate to="/access-denied" replace />;
    }
  }

  return children;
};

const ApplicationRoutes: React.FC = () => {
  const { isLoading, isAuthenticated, signIn } = useAuth();

  const userInfo = useAppSelector(selectUserInfo);

  React.useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      Logger.info("Initiating authentication flow for unauthenticated user", [
        "ApplicationRoutes",
      ]);
      signIn().catch((error) => {
        Logger.error("Authentication failed", error, ["ApplicationRoutes"]);
      });
    }
  }, [isAuthenticated, isLoading, signIn]);

  if (isLoading) {
    return <LoadingSpinner message="Initializing application..." />;
  }

  if (!isAuthenticated) {
    return <LoadingSpinner message="Authenticating..." />;
  }

  if (!userInfo) {
    return <LoadingSpinner message="Loading user information..." />;
  }

  return (
    <Routes>
      {routeConfigs.map((config) => (
        <Route
          key={config.path}
          path={config.path}
          element={
            <ProtectedRoute
              requiresAuth={config.requiresAuth}
              requiredRoles={config.requiredRoles}
              isAuthenticated={isAuthenticated}
            >
              <DefaultLayout>{config.element}</DefaultLayout>
            </ProtectedRoute>
          }
        />
      ))}
    </Routes>
  );
};

export default ApplicationRoutes;
